import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, interval, finalize, forkJoin, of } from 'rxjs';
import { switchMap, tap, catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { NotificationType, NotificationActionType, NOTIFICATION_TYPES, NOTIFICATION_ACTIONS, NotificationChannel } from '../shared/constants/notification.constants';
import { MyAccountService } from '../pages/my-account/services/my-acount.service';
import { MsalService } from '@azure/msal-angular';

export interface ServiceInfo {
  service_type: string;
  service_name: string;
  appointment_date?: string;
  location?: string;
  price?: number;
  duration?: string;
  vehicle?: {
    brand: string;
    model: string;
    license_plate: string;
  };
}

export interface NotificationAction {
  type: NotificationActionType;
  entity_id: string;
  entity_type: string;
}

export interface NotificationEnricher {
  serviceInfo: ServiceInfo;
  action: NotificationAction;
}

export interface EnrichedNotification extends Notification {
  enrichedMetadata: {
    serviceInfo?: ServiceInfo;
    action?: NotificationAction;
  };
}

export interface NotificationMetadata {
  user_data: {
    id: string;
    name: string;
    preferred_language: string;
    order_status: number;
    order_date: string;
    notification_metadata: InnerNotificationMetadata;
  };
  template_data: {
    message: string;
    order_date: string;
    order_status: number;
    brand?: string;
    model?: string;
    license_plate?: string;
  };
  vehicle_data?: {
    brand: string;
    model: string;
    license_plate: string;
    first_registration_date: string;
  };
  original_channels: string[];
  phone_number: null;
  whatsapp_template: null;
  template_variables: null;
}

export interface Notification {
  id: number;
  content: string;
  status: 'pending' | 'sent' | 'failed' | 'read';
  created_at: string;
  read_at?: string;
  notification_metadata: NotificationMetadata;
  template_data?: {
    brand?: string;
    model?: string;
    license_plate?: string;
    current_mileage?: number;
    remaining_mileage?: number;
    next_maintenance_date?: string;
    remaining_days?: number;
  };
  priority?: number;
  type: NotificationType;
  was_snoozed?: boolean;
  snooze_count?: number | null;
}

export interface InnerNotificationMetadata {
  service_info?: ServiceInfo;
  action?: NotificationAction;
  priority?: number;
}

interface UnreadCountResponse {
  count: number;
}

interface NotificationPollResponse {
  new: Notification[];
  expired: number[];
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  public notifications = new BehaviorSubject<Notification[]>([]);
  public hierarchyNotifications = new BehaviorSubject<Notification[]>([]);
  private headerUnreadCount = new BehaviorSubject<number>(0);
  private chatbotUnreadCount = new BehaviorSubject<number>(0);
  private pollingInterval = 30000; // 30 seconds
  private lastPollTime?: string;
  private readonly pageSize = 50;
  private readonly chatbotNotificationLimit = 15; // New constant for chatbot limit
  private isLoading = false;
  public notificationsOpen = new BehaviorSubject<boolean>(false);
  notificationsOpen$ = this.notificationsOpen.asObservable();
  private userId: string | null = null;

  private static readonly defaultEnricher = (notification: Notification): NotificationEnricher => ({
    serviceInfo: NotificationService.getDefaultServiceInfo(notification),
    action: NotificationService.getDefaultAction(notification)
  });

  public static readonly NOTIFICATION_ENRICHERS = new Map<NotificationType, (notification: Notification) => NotificationEnricher>([
    [NOTIFICATION_TYPES.SERVICE_UPDATE, NotificationService.defaultEnricher],
    [NOTIFICATION_TYPES.WASH_AFTER_RAIN, NotificationService.washEnricher],
    [NOTIFICATION_TYPES.WASH_BIWEEKLY, NotificationService.washEnricher]
  ]);

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private myAccountService: MyAccountService,
    private msalService: MsalService
  ) {
    // Wait for MSAL to be initialized before starting the service
    const initInterval = setInterval(() => {
      try {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          clearInterval(initInterval);
          this.userId = accounts[0].localAccountId;
          this.loadInitialNotifications('creation', this.notifications);
          this.loadInitialNotifications('hierarchy', this.hierarchyNotifications);
          this.startPolling();
        }
      } catch (error) {
      }
    }, 1000);

    // Clear interval after 30 seconds to prevent infinite polling
    setTimeout(() => clearInterval(initInterval), 30000);
  }

  private getUserId(): string {
    if (!this.userId) {
      throw new Error('User ID not initialized');
    }
    return this.userId;
  }

  private loadInitialNotifications(order: string, subject: BehaviorSubject<Notification[]>): void {
    this.isLoading = true;
    this.fetchNotifications(0, order, subject).pipe(
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe();
  }

  private startPolling(): void {
    interval(this.pollingInterval)
      .pipe(
        switchMap(() => forkJoin({
          creation: this.pollNewNotifications('creation', this.notifications),
          hierarchy: this.pollNewNotifications('hierarchy', this.hierarchyNotifications)
        }))
      )
      .subscribe();
  }

  private fetchNotifications(
    offset: number,
    order: string,
    subject: BehaviorSubject<Notification[]>
  ): Observable<Notification[]> {
    const channel = order === 'hierarchy' ? NotificationChannel.CHATBOT : NotificationChannel.HEADER;
    const params = {
      user_id: this.getUserId(),
      limit: order === 'hierarchy' ? this.chatbotNotificationLimit.toString() : this.pageSize.toString(),
      offset: offset.toString(),
      order: order,
      channel: channel,
      language: this.translateService.currentLang || 'es'
    };

    return this.http.get<Notification[]>(`${environment['NOTIFICATION_URL']}/notifications`, { params })
      .pipe(
        tap(notifications => {
          if (offset === 0) {
            subject.next(notifications);
          } else {
            const current = subject.value;
            subject.next([...current, ...notifications]);
          }
          this.updateUnreadCount(channel);
        }),
        catchError(error => {
          console.error('Error in fetchNotifications:', error);
          throw error;
        })
      );
  }

  private pollNewNotifications(
    order: string,
    subject: BehaviorSubject<Notification[]>
  ): Observable<NotificationPollResponse> {
    const channel = order === 'hierarchy' ? NotificationChannel.CHATBOT : NotificationChannel.HEADER;
    const params = {
      user_id: this.getUserId(),
      last_poll_time: this.lastPollTime || new Date().toISOString(),
      order: order,
      limit: order === 'hierarchy' ? this.chatbotNotificationLimit.toString() : this.pageSize.toString(),
      channel: channel,
      language: this.translateService.currentLang || 'es'
    };

    return this.http.get<NotificationPollResponse>(`${environment['NOTIFICATION_URL']}/notifications/poll`, { params })
      .pipe(
        tap(response => {
          if (response.new.length > 0 || response.expired.length > 0) {
            const current = subject.value;

            // Create a Set of existing notification IDs for quick lookup
            const existingIds = new Set(current.map(n => n.id));

            // Filter out duplicates from new notifications
            const uniqueNewNotifications = response.new.filter(n => !existingIds.has(n.id));

            if (uniqueNewNotifications.length > 0 || response.expired.length > 0) {
              // Remove expired notifications and add new ones
              const nonExpiredExisting = current.filter(n => !response.expired.includes(n.id));
              const updated = [...uniqueNewNotifications, ...nonExpiredExisting];

              if (order === 'hierarchy') {
                subject.next(updated.slice(0, this.chatbotNotificationLimit));
              } else {
                subject.next(updated);
              }
              this.updateUnreadCount(channel);
            }
          }
          this.lastPollTime = new Date().toISOString();
        }),
        catchError(error => {
          console.error('Error polling notifications:', error);
          throw error;
        })
      );
  }

  private updateUnreadCount(channel: NotificationChannel = NotificationChannel.HEADER): void {
    this.http.get<UnreadCountResponse>(`${environment['NOTIFICATION_URL']}/notifications/unread/count`, {
      params: {
        user_id: this.getUserId(),
        channel: channel
      }
    }).subscribe(
      response => {
        if (channel === NotificationChannel.HEADER) {
          this.headerUnreadCount.next(response.count);
        } else {
          this.chatbotUnreadCount.next(response.count);
        }
      },
      error => console.error('Error fetching unread count:', error)
    );
  }

  getNotifications(): Observable<Notification[]> {
    return this.notifications.asObservable();
  }

  getChatbotNotifications(): Observable<Notification[]> {
    return this.hierarchyNotifications.asObservable();
  }

  getUnreadCount(channel: NotificationChannel = NotificationChannel.HEADER): Observable<number> {
    return channel === NotificationChannel.HEADER ?
      this.headerUnreadCount.asObservable() :
      this.chatbotUnreadCount.asObservable();
  }

  loadMoreNotifications(): Observable<Notification[]> {
    const currentCount = this.notifications.value.length;
    return this.fetchNotifications(currentCount, 'creation', this.notifications);
  }

  markAsRead(notificationId: number): Observable<void> {
    return this.http.post<void>(
      `${environment['NOTIFICATION_URL']}/notifications/${notificationId}/read`,
      {}
    ).pipe(
      tap(() => {
        [this.notifications, this.hierarchyNotifications].forEach(subject => {
          const current = subject.value;
          const updated = current.map(n =>
            n.id === notificationId
              ? { ...n, read_at: new Date().toISOString(), status: 'read' as const }
              : n
          );
          subject.next(updated);
        });

        const newUnreadCount = this.notifications.value.filter(n => !n.read_at).length;
        this.updateUnreadCount();
      }),
      catchError(error => {
        console.error('Error marking notification as read:', error);
        throw error;
      })
    );
  }

  getUnreadNotifications(limit: number = 50, offset: number = 0): Observable<any[]> {
    return this.http.get<any[]>(`${environment['NOTIFICATION_URL']}/notifications/unread`, {
      params: {
        user_id: this.getUserId(),
        limit: limit.toString(),
        offset: offset.toString()
      }
    });
  }

  private static getDefaultServiceInfo(notification: Notification): ServiceInfo {
    return {
      service_type: notification.notification_metadata?.user_data?.notification_metadata?.service_info?.service_type || 'Unknown Service',
      service_name: notification.notification_metadata?.user_data?.notification_metadata?.service_info?.service_name || 'Unknown',
      appointment_date: notification.notification_metadata?.user_data?.notification_metadata?.service_info?.appointment_date || '',
      vehicle: {
        brand: notification.template_data?.brand || notification.notification_metadata?.vehicle_data?.brand || '',
        model: notification.template_data?.model || notification.notification_metadata?.vehicle_data?.model || '',
        license_plate: notification.template_data?.license_plate || notification.notification_metadata?.vehicle_data?.license_plate || ''
      }
    };
  }

  private static getDefaultAction(notification: Notification): NotificationAction {
    return notification.notification_metadata?.user_data?.notification_metadata?.action || {
      type: NOTIFICATION_ACTIONS.VIEW_SERVICE,
      entity_id: '',
      entity_type: 'unknown'
    };
  }

  public static getEnricher(type: NotificationType | undefined): (notification: Notification) => NotificationEnricher {
    if (!type) return this.defaultEnricher;
    return this.NOTIFICATION_ENRICHERS.get(type) || this.defaultEnricher;
  }

  private readonly ACTION_ROUTES = new Map<NotificationActionType, string[]>([
    [NOTIFICATION_ACTIONS.VIEW_SERVICE, ['/services']],
    [NOTIFICATION_ACTIONS.SCHEDULE_ITV, ['new-service']],
    [NOTIFICATION_ACTIONS.SCHEDULE_WASH, ['new-service']]
  ]);

  private readonly ACTION_TEXTS = new Map<NotificationActionType, string>([
    [NOTIFICATION_ACTIONS.VIEW_SERVICE, 'notifications.actions.view_service'],
    [NOTIFICATION_ACTIONS.SCHEDULE_ITV, 'notifications.actions.schedule_itv'],
    [NOTIFICATION_ACTIONS.SCHEDULE_WASH, 'notifications.actions.schedule_wash']
  ]);

  public getActionText(actionType: NotificationActionType | undefined): string {
    if (!actionType) return this.translateService.instant('notifications.actions.view_service');
    return this.translateService.instant(this.ACTION_TEXTS.get(actionType) || 'notifications.actions.view_service');
  }

  public getActionRoute(action: NotificationAction | undefined): string[] {
    if (!action?.type) return ['/services'];
    return this.ACTION_ROUTES.get(action.type as NotificationActionType) || ['/services'];
  }

  openNotifications(): void {
    this.notificationsOpen.next(true);
  }

  private static washEnricher(notification: Notification): NotificationEnricher {
    return {
      serviceInfo: {
        service_type: 'wash',
        service_name: notification.notification_metadata?.user_data?.notification_metadata?.service_info?.service_name || 'Lavado',
        vehicle: {
          brand: notification.template_data?.brand || notification.notification_metadata?.vehicle_data?.brand || '',
          model: notification.template_data?.model || notification.notification_metadata?.vehicle_data?.model || '',
          license_plate: notification.template_data?.license_plate || notification.notification_metadata?.vehicle_data?.license_plate || ''
        }
      },
      action: {
        type: NOTIFICATION_ACTIONS.SCHEDULE_WASH,
        entity_id: '',
        entity_type: 'wash'
      }
    };
  }

  markAllAsRead(): Observable<void> {
    const unreadNotifications = this.notifications.value.filter(n => !n.read_at);

    // If no unread notifications, return empty observable
    if (unreadNotifications.length === 0) {
      return of(void 0);
    }

    // Create an array of observables for each mark as read operation
    const markAsReadObservables = unreadNotifications.map(notification =>
      this.markAsRead(notification.id)
    );

    // Execute all mark as read operations in parallel
    return forkJoin(markAsReadObservables).pipe(
      map(() => void 0),
      catchError(error => {
        console.error('Error marking all notifications as read:', error);
        throw error;
      })
    );
  }

  /**
   * Snooze a notification for 24 hours
   * @param notificationId The ID of the notification to snooze
   * @returns An Observable that completes when the notification is snoozed
   */
  snoozeNotification(notificationId: number): Observable<void> {
    return this.http.post<void>(
      `${environment['NOTIFICATION_URL']}/notifications/${notificationId}/snooze`,
      {}
    ).pipe(
      tap(() => {
        // Remove the snoozed notification from both lists
        // The notification will reappear later with was_snoozed flag
        const removeNotification = (notifications: Notification[]): Notification[] => {
          return notifications.filter(notification => notification.id !== notificationId);
        };

        // Update both notification lists
        this.notifications.next(removeNotification(this.notifications.value));
        this.hierarchyNotifications.next(removeNotification(this.hierarchyNotifications.value));

        // Update unread counts if needed
        this.updateUnreadCount(NotificationChannel.HEADER);
        this.updateUnreadCount(NotificationChannel.CHATBOT);
      }),
      catchError(error => {
        console.error('Error in snoozeNotification:', error);
        throw error;
      })
    );
  }
} 